import React from 'react';
import { Insight } from 'libs/end-to-end/src/insights';

type InsightType = 'column' | 'row' | 'fullWidthRow';

export interface InsightGroupProps {
  insights: Insight[];
  type: InsightType;
}

type GroupLength = 0 | 1 | 2 | 3 | 4;

interface GroupMetadata {
  type: InsightType;
  numInsights: number;
}

const groupIndexToGroupMetadata: Record<GroupLength, GroupMetadata> = {
  0: { type: 'column', numInsights: 1 },
  1: { type: 'column', numInsights: 2 },
  2: { type: 'column', numInsights: 3 },
  3: { type: 'row', numInsights: 4 },
  4: { type: 'row', numInsights: 4 },
};

const defaultGroupMetadata: GroupMetadata = {
  type: 'fullWidthRow',
  numInsights: 1,
};

const isGroupIndex = (
  groupedInsightsLength: number,
): groupedInsightsLength is GroupLength => {
  return groupedInsightsLength in groupIndexToGroupMetadata;
};

const getGroupMetadata = (groupedInsightsLength: number): GroupMetadata => {
  if (isGroupIndex(groupedInsightsLength)) {
    return groupIndexToGroupMetadata[groupedInsightsLength];
  }

  return defaultGroupMetadata;
};

export const groupInsights = (insights: Insight[]): InsightGroupProps[] => {
  const groupedInsights: InsightGroupProps[] = [];

  for (const insight of insights) {
    const lastGroupIndex = groupedInsights.length - 1;
    const lastGroup = groupedInsights[lastGroupIndex];

    // Math.abs to avoid -1 on the first iteration
    const { numInsights } = getGroupMetadata(Math.abs(lastGroupIndex));

    const shouldCreateNewGroup =
      !lastGroup || lastGroup.insights.length >= numInsights;

    if (shouldCreateNewGroup) {
      // If we create a new group, we need to use the next group type
      const { type } = getGroupMetadata(lastGroupIndex + 1);

      groupedInsights.push({
        insights: [insight],
        type,
      });
    } else {
      lastGroup.insights.push(insight);
    }
  }

  return groupedInsights;
};

export type InsightSearchParams = Pick<Insight, 'type'> & {
  // displayName
  dimension: string;
  // displayName
  treatmentLevel: string;
};

/**
 * Returns a unique string based on the properties of the insight
 */
export const getInsightKey = (insight: Insight): string => {
  return insight.type
    .concat(insight.treatmentLevel)
    .concat(insight.referenceLevel)
    .concat(insight.dimension)
    .concat(insight.metric);
};

export const sortByPriority = (insights: Insight[]): Insight[] => {
  return [...insights].sort((a, b) => {
    if (a.priority && b.priority) {
      return a.priority - b.priority;
    }

    return 0;
  });
};

const removeCurrentInsightIfPresent = ({
  currentInsight,
  insights,
}: {
  currentInsight?: Insight;
  insights: Insight[];
}): Insight[] => {
  if (!currentInsight) {
    return insights;
  }

  return insights.filter(
    (x) => getInsightKey(x) !== getInsightKey(currentInsight),
  );
};

/**
 * Returns a list of insights that match the search parameters. We incrementally try
 * to match more parameters if we still have results to make them even closer. But if we filter out too much, we just return the results we have from the previous step.
 */
export const getRelatedInsights = ({
  currentInsight,
  filters,
  insights,
  maxInsights,
}: {
  currentInsight?: Insight;
  insights: Insight[];
  filters: { metric: string | null; dimension: string | null };
  maxInsights: number;
}): Insight[] => {
  const insightsMatchingMetric = insights.filter(
    (i) => i.metric === filters.metric,
  );

  if (insightsMatchingMetric.length <= maxInsights) {
    return removeCurrentInsightIfPresent({
      currentInsight,
      insights: insights.slice(0, maxInsights),
    });
  }

  const insightsMatchingMetricAndDimension = insights.filter(
    (i) => filters.dimension === i.dimension && filters.metric === i.metric,
  );

  if (insightsMatchingMetricAndDimension.length <= maxInsights) {
    return removeCurrentInsightIfPresent({
      currentInsight,
      insights: insightsMatchingMetricAndDimension.slice(0, maxInsights),
    });
  }

  return removeCurrentInsightIfPresent({
    currentInsight,
    insights: insightsMatchingMetric.slice(0, maxInsights),
  });
};
